import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import Map from '../components/Map';

interface PinData {
  lat: number;
  lng: number;
  address?: string;
  placeId?: string;
  name?: string;
}

function MapPage() {
  const { logout, user } = useAuth();
  const [selectedLocation, setSelectedLocation] = useState<PinData | null>(null);
  const [submittedLocation, setSubmittedLocation] = useState<PinData | null>(null);

  const handlePinDropped = (pinData: PinData) => {
    console.log('Pin dropped:', pinData);
    setSelectedLocation(pinData);
  };

  const handleLocationSelected = (location: PinData) => {
    console.log('Location selected for submission:', location);
    setSubmittedLocation(location);
    
    // Here you would typically send this data to your backend
    // Example API call:
    // await api.post('/locations', {
    //   latitude: location.lat,
    //   longitude: location.lng,
    //   address: location.address,
    //   name: location.name
    // });
  };

  return (
    <div>
      <nav className="navbar">
        <div className="navbar-brand">🗺️ RouteShare Maps</div>
        <ul className="navbar-nav">
          <li><Link to="/" className="nav-link">🏠 Home</Link></li>
          <li><Link to="/dashboard" className="nav-link">🚗 Dashboard</Link></li>
          <li><Link to="/chat" className="nav-link">💬 Chat</Link></li>
          <li><span className="nav-link">Welcome, {[user?.first_name, user?.last_name].filter(Boolean).join(' ')}!</span></li>
          <li><button onClick={logout} className="nav-link" style={{ background: 'none', border: 'none', cursor: 'pointer' }}>Logout</button></li>
        </ul>
      </nav>

      <main className="main-content">
        <div className="max-w-6xl mx-auto py-8 px-4">
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-gray-900 mb-2">🗺️ Interactive Map</h1>
            <p className="text-gray-600">
              Search for locations, drop pins, and submit coordinates for your routes and trips.
            </p>
          </div>

          {/* Map Component */}
          <Map
            onPinDropped={handlePinDropped}
            onLocationSelected={handleLocationSelected}
            initialCenter={{ lat: -1.2864, lng: 36.8172 }} // Nairobi, Kenya
            zoom={12}
            height="500px"
            className="mb-8"
          />

          {/* Results Section */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Current Selection */}
            <div className="bg-white p-6 rounded-lg shadow-sm border">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">📍 Current Selection</h2>
              {selectedLocation ? (
                <div className="space-y-3">
                  <div className="p-4 bg-blue-50 rounded-lg">
                    <h3 className="font-medium text-blue-900 mb-2">
                      {selectedLocation.name || 'Unnamed Location'}
                    </h3>
                    <p className="text-sm text-blue-800 mb-2">
                      {selectedLocation.address || 'No address available'}
                    </p>
                    <div className="flex justify-between items-center">
                      <span className="text-xs text-blue-600 font-mono">
                        {selectedLocation.lat.toFixed(6)}, {selectedLocation.lng.toFixed(6)}
                      </span>
                      <button
                        onClick={() => {
                          const coords = `${selectedLocation.lat}, ${selectedLocation.lng}`;
                          navigator.clipboard.writeText(coords);
                          alert('Coordinates copied to clipboard!');
                        }}
                        className="text-xs bg-blue-600 text-white px-2 py-1 rounded hover:bg-blue-700"
                      >
                        Copy
                      </button>
                    </div>
                  </div>
                </div>
              ) : (
                <p className="text-gray-500 italic">
                  No location selected. Click on the map or search to select a location.
                </p>
              )}
            </div>

            {/* Submitted Location */}
            <div className="bg-white p-6 rounded-lg shadow-sm border">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">✅ Submitted Location</h2>
              {submittedLocation ? (
                <div className="space-y-3">
                  <div className="p-4 bg-green-50 rounded-lg">
                    <h3 className="font-medium text-green-900 mb-2">
                      {submittedLocation.name || 'Unnamed Location'}
                    </h3>
                    <p className="text-sm text-green-800 mb-2">
                      {submittedLocation.address || 'No address available'}
                    </p>
                    <div className="text-xs text-green-600 font-mono">
                      Lat: {submittedLocation.lat.toFixed(6)}<br />
                      Lng: {submittedLocation.lng.toFixed(6)}
                    </div>
                    {submittedLocation.placeId && (
                      <div className="text-xs text-green-600 mt-2">
                        Place ID: {submittedLocation.placeId}
                      </div>
                    )}
                  </div>
                  
                  {/* Example API Data */}
                  <div className="bg-gray-50 p-3 rounded">
                    <h4 className="text-sm font-medium text-gray-700 mb-2">API Payload Example:</h4>
                    <pre className="text-xs text-gray-600 overflow-x-auto">
{JSON.stringify({
  latitude: submittedLocation.lat,
  longitude: submittedLocation.lng,
  address: submittedLocation.address,
  name: submittedLocation.name,
  place_id: submittedLocation.placeId
}, null, 2)}
                    </pre>
                  </div>
                </div>
              ) : (
                <p className="text-gray-500 italic">
                  No location submitted yet. Select a location and click "Use This Location" to submit.
                </p>
              )}
            </div>
          </div>

          {/* Integration Examples */}
          <div className="mt-8 bg-white p-6 rounded-lg shadow-sm border">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">🔧 Integration Examples</h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h3 className="font-medium text-gray-800 mb-2">For Route Creation:</h3>
                <div className="bg-gray-50 p-3 rounded text-sm">
                  <code className="text-blue-600">
                    {`<Map
  onLocationSelected={(location) => {
    setFormData(prev => ({
      ...prev,
      start_latitude: location.lat,
      start_longitude: location.lng,
      start_location: location.address
    }));
  }}
/>`}
                  </code>
                </div>
              </div>

              <div>
                <h3 className="font-medium text-gray-800 mb-2">For Stop Points:</h3>
                <div className="bg-gray-50 p-3 rounded text-sm">
                  <code className="text-green-600">
                    {`<Map
  onLocationSelected={(location) => {
    addStopPoint({
      name: location.name,
      latitude: location.lat,
      longitude: location.lng,
      address: location.address
    });
  }}
/>`}
                  </code>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}

export default MapPage;
